import { UserFragment } from "$gql/fragments/general/User.gen";
import { CreateUserBrandInterest } from "$gql/mutations/general/CreateUserBrandInterest.gen";
import { SubmitEmailForPerks } from "$gql/mutations/general/SubmitEmailForPerks.gen";
import Button from "components/core/Button/Button";
import TextInput from "components/core/Forms/TextInput/TextInput";
import Markdown from "components/Markdown/Markdown";
import TiiCKERPerksCTA from "components/TiiCKERPerks/CallToAction/TiiCKERPerksCTA";
import Cookies from "js-cookie";
import React, { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { triggerBrandEmailSubmission } from "source/analytics-events";
import {
  CookieName,
  EmailSubmission,
  useCookie,
  useMutationBundle,
} from "source/hooks";
import styles from "./VoteForPerks.module.scss";

type Props = {
  ticker: string;
  brandName: string;
  signedInUser?: UserFragment;
  userInterestedInPerks: boolean;
  tiickerPerksQualified?: string;
  tiickerPerksSignedOut?: string;
  perkOverrideMediaUrl?: string;
  perkVoteOverrideText?: string;
  perkVoteDisclaimerText?: string;
};

type EmailSubmit = {
  email: string;
};

const VoteForPerks = (props: Props) => {
  const [successSubmit, setSuccessSubmit] = useState<boolean | undefined>();

  const inputRef = useRef<null | HTMLInputElement>(null);

  const emailCookie = useCookie<EmailSubmission>(CookieName.SubmittedEmail);
  const userEmail = props.signedInUser?.email || emailCookie?.email;
  const hasVoted =
    props.userInterestedInPerks ||
    emailCookie?.perksVotedFor.some(
      (p) => p.tickerSymbol.toLowerCase() === props.ticker.toLowerCase()
    );

  const [submitEmail] = useMutationBundle(SubmitEmailForPerks);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);

  const { handleSubmit, register, errors, formState, setValue } =
    useForm<EmailSubmit>({
      defaultValues: {
        email: userEmail,
      },
    });

  const [createUserInterest] = useMutationBundle(CreateUserBrandInterest);
  const onUserInterested = useCallback(async () => {
    const id = props.signedInUser?.id || 0;
    const result = await createUserInterest({
      variables: { userId: id, tickerSymbol: props.ticker },
    });
    const updatedInterest = result.data?.createUserBrandInterest;
    if (updatedInterest) {
      true;
    }
    return updatedInterest || null;
  }, []);

  const onSubmit = useCallback(async (data: EmailSubmit) => {
    try {
      const result = await submitEmail({
        variables: {
          email: data.email,
          ticker: props.ticker,
        },
      });

      const interestQuery = props.signedInUser
        ? await onUserInterested()
        : null;

      if (result.data?.submitEmailForPerks) {
        triggerBrandEmailSubmission(props.ticker);

        setSuccessSubmit(true);
        setValue("email", "");
        setHasSubmittedEmail(true);

        const existingCookie: EmailSubmission = emailCookie
          ? emailCookie
          : {
              email: data.email,
              perksVotedFor: [],
            };

        const newCookie: EmailSubmission = {
          ...existingCookie,
          perksVotedFor: [
            ...existingCookie.perksVotedFor,
            { tickerSymbol: props.ticker },
          ],
        };

        Cookies.set(CookieName.SubmittedEmail, newCookie);
      } else {
        setSuccessSubmit(false);
      }
    } catch (e) {
      setSuccessSubmit(false);
    }
  }, []);

  const isCarnival =
    props.ticker.toUpperCase() === "CCL" ||
    props.ticker.toUpperCase() === "CUK";

  const isDlpn = props.ticker.toUpperCase() === "DLPN";

  return (
    <div className="container">
      <div className={styles.container}>
        <div className={styles.voteContainer}>
          {isCarnival ? (
            <h3 className={styles.title}>Carnival Has Perks!</h3>
          ) : isDlpn ? (
            <h3 className={styles.title}>
              Be the first to ride the wave of exclusive perks!
            </h3>
          ) : (
            <h3 className={styles.title}>Vote for {props.brandName} Perks!</h3>
          )}
          {!(successSubmit || hasVoted) &&
            (props.perkVoteOverrideText &&
            props.perkVoteOverrideText.trim().length > 0 ? (
              <Markdown markdownText={props.perkVoteOverrideText} />
            ) : isCarnival ? (
              <p className={styles.body}>
                Visit their investor relations page at{" "}
                <a href="http://CarnivalCorp.com">CarnivalCorp.com </a>
                or tell them I WANT CARNIVAL PERKS ON TiiCKER. Vote by entering
                your email below.
              </p>
            ) : isDlpn ? (
              <p className={styles.body}>
                Join the Dolphin community to get priority access to shareholder
                benefits that make a splash. Don't miss out-enter your email
                today and stay ahead of the tide!
              </p>
            ) : (
              <p className={styles.body}>
                Looks like {props.brandName} doesn't offer shareholder perks
                yet... tell {props.brandName} (Tii:{props.ticker.toUpperCase()})
                you want perks by adding your email below.
              </p>
            ))}

          {successSubmit === undefined && !hasVoted && (
            <form
              className={styles.inputContainer}
              onSubmit={handleSubmit(onSubmit)}
            >
              <TextInput
                register={register({
                  required: "Email is required.",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: "Invalid email address",
                  },
                })}
                wrapperClass={styles.inputWrapper}
                className={styles.input}
                placeholder="Email Address"
                name="email"
              />
              <Button type="primary" submit>
                {isDlpn ? "Submit" : "Vote"}
              </Button>
            </form>
          )}
          {(successSubmit || hasVoted) && (
            <p>
              Thank you! Submission received. We will let you know when Tii:
              {props.ticker.toUpperCase()} has Perks!
            </p>
          )}
          {successSubmit === false && (
            <p>
              There was a problem submitting your email, please contact
              service@tiicker.com
            </p>
          )}
          {props.perkVoteDisclaimerText && !(successSubmit || hasVoted) && (
            <p className={styles.disclaimer}>{props.perkVoteDisclaimerText}</p>
          )}
        </div>
        {props.perkOverrideMediaUrl &&
        props.perkOverrideMediaUrl.trim().length > 0 ? (
          <img
            className={styles.perkOverrideMedia}
            src={props.perkOverrideMediaUrl}
          />
        ) : (
          <TiiCKERPerksCTA
            vertical
            loggedOutContent={props.tiickerPerksSignedOut}
            qualifyContent={props.tiickerPerksQualified}
          />
        )}
      </div>
    </div>
  );
};

export default VoteForPerks;
